import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import html2canvas from "html2canvas";
import { Download, Award, BookOpen, TrendingUp, Star, ExternalLink, Crown, Megaphone, Calculator, Cog, Mic } from "lucide-react";
import AIBusinessPlan from "@/components/AIBusinessPlan";
import Certificate from "@/components/Certificate";
import DISCAssessmentUpsell from "@/components/DISCAssessmentUpsell";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mirror avatar/role catalogs from StudentProfileModal so the parent view
// can render the chosen avatar without an extra network roundtrip.
const AVATAR_EMOJI = {
  lemon: "🍋", rocket: "🚀", star: "⭐", sparkles: "✨",
  fire: "🔥", lightning: "⚡", globe: "🌍", leaf: "🌱",
  trophy: "🏆", target: "🎯", rainbow: "🌈", music: "🎵",
};
const TEAM_ROLE_META = {
  leader:     { label: "Leader",     icon: Crown,      blurb: "Sets the vision and makes the call." },
  marketing:  { label: "Marketing",  icon: Megaphone,  blurb: "Builds the brand and finds customers." },
  finance:    { label: "Finance",    icon: Calculator, blurb: "Owns costs, prices, and profit." },
  operations: { label: "Operations", icon: Cog,        blurb: "Keeps the day-to-day running smoothly." },
};

function ParentAccessPage() {
  const { shareToken } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const certificateRef = useRef(null);

  // Persist parent-view language locally so re-visits stay in the chosen lang
  useEffect(() => {
    const saved = localStorage.getItem("parentViewLang");
    if (saved && saved !== i18n.language) {
      i18n.changeLanguage(saved);
    }
  }, [i18n]);

  const switchLang = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem("parentViewLang", lang);
  };

  useEffect(() => {
    loadParentAccessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareToken]);

  const loadParentAccessData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/parent-access/${shareToken}`);
      setData(response.data);
    } catch (err) {
      console.error("Error loading parent access data:", err);
      if (err.response?.status === 404) {
        setError("This access link is not valid or has been removed.");
      } else if (err.response?.status === 410) {
        setError("This access link has expired. Please ask for a new link.");
      } else {
        setError("Failed to load data. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCertificate = async () => {
    if (!certificateRef.current) return;
    
    try {
      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff"
      });
      
      const link = document.createElement("a");
      link.download = `${data.student_name}_Certificate.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading certificate:", err);
    }
  };

  const handleDownloadBusinessPlanPDF = () => {
    // Stream the public PDF directly from the API. Keeps the call simple
    // and avoids any auth header — this endpoint is public.
    setDownloadingPDF(true);
    const url = `${API}/parent-access/${shareToken}/business-plan.pdf`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => setDownloadingPDF(false), 1200);
  };

  // Inject Open Graph meta tags so unfurlers (iMessage, Slack, FB, X) show the branded card.
  useEffect(() => {
    if (!data) return;
    const firstName = data.student_first_name || (data.student_name || "Student").split(" ")[0];
    const biz = data.student_business_name || "My Mini Business";
    const title = `${biz} — Mini Business Plan`;
    const desc = `Cheer on ${firstName}! See ${firstName}'s entrepreneurship journey from the Lemonade Stand Business Plan.`;
    const ogImage = `${API}/parent-access/${shareToken}/og-image.png`;
    const url = `${window.location.origin}/parent/${shareToken}`;

    const prevTitle = document.title;
    document.title = title;

    const tags = [
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Lemonade Stand Business Plan" },
      { property: "og:title", content: title },
      { property: "og:description", content: desc },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: desc },
      { name: "twitter:image", content: ogImage },
      { name: "description", content: desc },
    ];
    const created = [];
    tags.forEach(({ property, name, content }) => {
      const sel = property ? `meta[property="${property}"]` : `meta[name="${name}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (property) el.setAttribute("property", property);
        if (name) el.setAttribute("name", name);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    });
    return () => {
      document.title = prevTitle;
      created.forEach((el) => el.remove());
    };
  }, [data, shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your child's progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex flex-col">
        <div className="flex-1 container mx-auto px-4 py-16">
          <div className="max-w-md mx-auto text-center">
            <div className="text-6xl mb-6">😕</div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Oops!</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-yellow-500 text-white rounded-lg font-semibold hover:bg-yellow-600 transition-colors"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const tabs = [
    { id: "overview", label: "Overview", icon: TrendingUp },
    { id: "business-plan", label: "Business Plan", icon: BookOpen },
    { id: "certificate", label: "Certificate", icon: Award },
    { id: "workbook", label: "Workbook", icon: Star }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-yellow-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🍋</span>
              <span className="font-bold text-gray-800">Lemonade Stand Business Plan</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>👨‍👩‍👧</span>
              <span>Parent View</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 container mx-auto px-4 py-8">
        {/* Welcome Banner — parent-friendly, personalized */}
        {(() => {
          const firstName = data.student_first_name || (data.student_name || "Student").split(" ")[0];
          const businessName = data.student_business_name;
          const roleKey = data.student_team_role;
          const role = roleKey && TEAM_ROLE_META[roleKey];
          const RoleIcon = role && role.icon;
          const avKey = (data.student_avatar || "").replace(/^preset:/, "");
          const avEmoji = AVATAR_EMOJI[avKey];
          return (
            <div
              className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 rounded-3xl shadow-2xl p-6 sm:p-8 mb-8 text-white relative overflow-hidden"
              data-testid="parent-view-hero"
            >
              <div className="absolute -top-6 -right-6 text-9xl opacity-20 select-none" aria-hidden="true">🎉</div>
              <div className="relative flex flex-col md:flex-row items-center gap-5">
                <div className="flex-shrink-0">
                  {data.logo?.logo_data ? (
                    <img
                      src={data.logo.logo_data}
                      alt={`${firstName}'s business logo`}
                      className="w-24 h-24 rounded-2xl object-cover shadow-lg border-2 border-white/40"
                    />
                  ) : (
                    <div
                      className="w-24 h-24 rounded-2xl bg-white/95 flex items-center justify-center text-5xl shadow-lg border-2 border-white/40"
                      aria-hidden="true"
                    >
                      {avEmoji || "🍋"}
                    </div>
                  )}
                </div>
                <div className="flex-1 text-center md:text-left">
                  <p className="text-xs sm:text-sm font-bold tracking-wider opacity-95 uppercase mb-1">
                    {t("parentView.noteFromTeacher", { firstName })}
                  </p>
                  <h1
                    className="text-2xl md:text-4xl font-bold leading-tight mb-1"
                    data-testid="parent-view-heading"
                  >
                    {t("parentView.buildingABusiness", { firstName })}
                  </h1>
                  {businessName && (
                    <p
                      className="text-base md:text-lg font-semibold opacity-95"
                      data-testid="parent-view-business-name"
                    >
                      {t("parentView.ceoOf")} <span className="bg-white/25 backdrop-blur-sm px-2 py-0.5 rounded-md">{businessName}</span>
                    </p>
                  )}
                  {role && (
                    <span
                      className="mt-2 inline-flex items-center gap-1 text-xs font-bold bg-white text-purple-700 border border-purple-300 rounded-full px-2.5 py-1"
                      data-testid="parent-view-role-badge"
                    >
                      <RoleIcon className="w-3.5 h-3.5" aria-hidden="true" />
                      {role.label}
                    </span>
                  )}
                  <p className="mt-3 text-sm md:text-base opacity-95">
                    {t("parentView.intro", { firstName })}
                  </p>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <div className="text-center bg-white/20 backdrop-blur-sm rounded-2xl px-5 py-3 min-w-[120px]">
                    <div
                      className="text-4xl font-bold"
                      data-testid="parent-view-completion-pct"
                    >
                      {data.progress.completion_percentage}%
                    </div>
                    <p className="text-xs uppercase tracking-wide opacity-95">{t("parentView.complete")}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleDownloadBusinessPlanPDF}
                    disabled={downloadingPDF}
                    className="inline-flex items-center gap-2 bg-white text-purple-700 px-4 py-2.5 rounded-xl font-bold text-sm shadow-md hover:shadow-xl transform hover:scale-[1.02] transition-all disabled:opacity-60"
                    data-testid="parent-download-business-plan-btn"
                  >
                    <Download className="w-4 h-4" />
                    {downloadingPDF ? t("parentView.opening") : t("parentView.downloadPdf")}
                  </button>
                  {/* Language toggle */}
                  <div
                    className="inline-flex items-center bg-white/20 backdrop-blur-sm rounded-full text-[11px] font-bold overflow-hidden"
                    role="group"
                    aria-label="Language"
                    data-testid="parent-view-lang-toggle"
                  >
                    <button
                      type="button"
                      onClick={() => switchLang("en")}
                      className={`px-2.5 py-1 ${i18n.language?.startsWith("en") ? "bg-white text-purple-700" : "text-white"}`}
                      data-testid="parent-view-lang-en"
                      aria-pressed={i18n.language?.startsWith("en")}
                    >EN</button>
                    <button
                      type="button"
                      onClick={() => switchLang("es")}
                      className={`px-2.5 py-1 ${i18n.language?.startsWith("es") ? "bg-white text-purple-700" : "text-white"}`}
                      data-testid="parent-view-lang-es"
                      aria-pressed={i18n.language?.startsWith("es")}
                    >ES</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Pitch Readiness — only shown if the student has practiced their pitch */}
        {data.pitch_summary && data.pitch_summary.attempts > 0 && (
          <div
            className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-purple-100"
            data-testid="parent-pitch-readiness"
          >
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                  <Mic className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">{t("parentView.pitchReadiness")}</h2>
                  <p className="text-xs text-gray-500">
                    {t("parentView.pitchReadinessDesc", { firstName: data.student_first_name || "your child" })}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-purple-600">{data.pitch_summary.best_pct || 0}%</div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">{t("parentView.pitchBest")}</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${data.pitch_summary.delta > 0 ? "text-emerald-600" : "text-gray-600"}`}>
                    {data.pitch_summary.delta > 0 ? "+" : ""}{data.pitch_summary.delta || 0}%
                  </div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">{t("parentView.pitchGrowth")}</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-700">{data.pitch_summary.attempts}</div>
                  <div className="text-[11px] uppercase tracking-wide text-gray-500">{t("parentView.pitchPractices")}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-yellow-500 text-white"
                    : "bg-white text-gray-600 hover:bg-yellow-100"
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            {/* Progress Summary */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-yellow-500" />
                Progress Summary
              </h2>
              
              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>{data.progress.completed_lessons} of {data.progress.total_lessons} lessons completed</span>
                  <span>{data.progress.completion_percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div 
                    className="bg-gradient-to-r from-yellow-400 to-orange-400 h-4 rounded-full transition-all duration-500"
                    style={{ width: `${data.progress.completion_percentage}%` }}
                  ></div>
                </div>
              </div>

              {/* Modules Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.modules.map((module, index) => (
                  <div 
                    key={module.id}
                    className={`p-4 rounded-xl border-2 ${
                      module.is_complete 
                        ? "border-green-300 bg-green-50" 
                        : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">
                        {module.is_complete ? "✅" : "📚"}
                      </span>
                      <span className="font-semibold text-gray-800">
                        Session {index + 1}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                      {module.title.replace(`Session ${index + 1}: `, "")}
                    </p>
                    <div className="text-xs text-gray-500">
                      {module.completed_lessons}/{module.total_lessons} lessons
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Badges */}
            {data.student_badges && data.student_badges.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Earned Badges
                </h2>
                <div className="flex flex-wrap gap-3">
                  {data.student_badges.map((badge, index) => (
                    <div 
                      key={index}
                      className="bg-gradient-to-br from-yellow-100 to-orange-100 border-2 border-yellow-400 rounded-xl p-3 text-center"
                    >
                      <div className="text-2xl mb-1">⭐</div>
                      <p className="text-xs font-semibold text-gray-800 capitalize">
                        {badge.replace(/_/g, ' ')}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DISC Assessment Upsell for Parents */}
            <DISCAssessmentUpsell variant="completion" hasDiscAssessment={false} />
          </div>
        )}

        {activeTab === "business-plan" && (
          <div>
            {data.ai_business_plan ? (
              <AIBusinessPlan 
                plan={data.ai_business_plan}
                studentName={data.student_name}
                showActions={true}
                canRegenerate={false}
                canShare={false}
                hasDiscAssessment={false}
              />
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="text-6xl mb-4">📝</div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Business Plan Coming Soon!
                </h2>
                <p className="text-gray-600">
                  {data.student_name} hasn't generated their AI Business Plan yet.
                  They'll create one as they complete the program!
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "certificate" && (
          <div>
            {data.progress.is_program_complete ? (
              <div>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={handleDownloadCertificate}
                    className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download Certificate
                  </button>
                </div>
                <div ref={certificateRef}>
                  <Certificate 
                    user={{ name: data.student_name, badges: data.student_badges }}
                    showActions={false}
                  />
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="text-6xl mb-4">🏆</div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Certificate In Progress
                </h2>
                <p className="text-gray-600 mb-4">
                  {data.student_name} is {data.progress.completion_percentage}% through the program.
                  They'll earn their certificate once they complete all lessons!
                </p>
                <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-gradient-to-r from-yellow-400 to-orange-400 h-3 rounded-full"
                    style={{ width: `${data.progress.completion_percentage}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "workbook" && (
          <div className="space-y-6">
            {data.workbook && data.workbook.length > 0 ? (
              data.workbook.map((entry, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-yellow-600 uppercase tracking-wide">
                      {entry.module_title}
                    </span>
                    <h3 className="text-lg font-bold text-gray-800">
                      {entry.lesson_title}
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {entry.answers.map((answer, aIndex) => (
                      <div key={aIndex} className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm font-medium text-gray-600 mb-1">
                          {answer.question_text || `Response ${aIndex + 1}`}
                        </p>
                        <p className="text-gray-800">
                          {answer.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="text-6xl mb-4">📔</div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Workbook Entries Coming Soon!
                </h2>
                <p className="text-gray-600">
                  {data.student_name}'s reflections and answers will appear here as they progress through the lessons.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-3">
              Proud of Your Young Entrepreneur?
            </h2>
            <p className="text-gray-600 mb-6">
              The Lemonade Stand Business Plan program teaches real-world business skills through hands-on learning. 
              Share this journey with family and friends!
            </p>
            <a
              href="https://www.lemonadestandbusinessplan.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-xl font-bold hover:from-yellow-500 hover:to-orange-500 transition-colors shadow-lg"
            >
              Learn More About the Program
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ParentAccessPage;
